// ---------------------------------------------------------------------------
// useAgentMutations — create / replace / patch / delete wrappers around
// AgentsService for the active workspace. Tracks a shared `isSaving` flag and
// surfaces the last error message; individual callers await the returned
// promises to react to success/failure.
// ---------------------------------------------------------------------------

import { useCallback, useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { AgentsService } from '../../services/agentsService.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../telemetry/userbi.js';
import type {
  Agent,
  AgentVersion,
  PatchAgentBody,
  UpsertAgentBody,
} from '../../types/agent.js';

import type {
  UseAgentMutationsParams,
  UseAgentMutationsResult,
} from './useAgentMutations.interface.js';

export function useAgentMutations(
  params: UseAgentMutationsParams,
): UseAgentMutationsResult {
  const { workspaceName } = params;
  const client = useHttpClient();
  const service = useMemo(() => new AgentsService(client), [client]);
  const { trackOperation } = useUserBi();

  const [inFlightMutations, setInFlightMutations] = useState(0);
  const isSaving = inFlightMutations > 0;
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const run = useCallback(
    async <T,>(
      operation: OperationType,
      op: (ws: string) => Promise<T>,
    ): Promise<T> => {
      if (workspaceName === null) {
        throw new Error('No workspace selected.');
      }
      setError(null);
      setInFlightMutations((count) => count + 1);
      try {
        return await trackOperation(
          {
            panelType: PanelType.AgentsPage,
            entityType: EntityType.Agent,
            operation,
          },
          () => op(workspaceName),
        );
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'The operation failed.';
        setError(message);
        throw err;
      } finally {
        setInFlightMutations((count) => Math.max(0, count - 1));
      }
    },
    [trackOperation, workspaceName],
  );

  const createAgent = useCallback(
    (name: string, body: UpsertAgentBody): Promise<Agent> =>
      run(OperationType.Create, (ws) => service.create(ws, name, body)),
    [run, service],
  );

  const replaceAgent = useCallback(
    (name: string, body: UpsertAgentBody): Promise<Agent> =>
      run(OperationType.Update, (ws) => service.replace(ws, name, body)),
    [run, service],
  );

  const patchAgent = useCallback(
    (name: string, body: PatchAgentBody): Promise<Agent> =>
      run(OperationType.Update, (ws) => service.patch(ws, name, body)),
    [run, service],
  );

  const deleteAgent = useCallback(
    (name: string): Promise<void> =>
      run(OperationType.Delete, (ws) => service.delete(ws, name)),
    [run, service],
  );

  const snapshotAgent = useCallback(
    (name: string): Promise<AgentVersion> =>
      run(OperationType.Snapshot, (ws) => service.snapshot(ws, name)),
    [run, service],
  );

  return {
    createAgent,
    replaceAgent,
    patchAgent,
    deleteAgent,
    snapshotAgent,
    isSaving,
    error,
    clearError,
  };
}
